import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Stage,
  Layer,
  Rect,
  Circle,
  Wedge,
  RegularPolygon,
  Star,
  Text,
  Line,
  Image,
  Group,
  Shape,
} from 'react-konva';
import useImage from 'use-image';
import { APP_FONT_FAMILIES, preloadAppFonts } from '../utils/fontPreloader';

const fillPatternCache = new Map();

const getShapeFillBounds = (shape) => {
  if (Number.isFinite(shape.width) && Number.isFinite(shape.height)) {
    return { x: 0, y: 0, width: Math.max(1, Math.abs(shape.width)), height: Math.max(1, Math.abs(shape.height)) };
  }

  const coordinatePoints = Array.isArray(shape.bezierPoints) && shape.bezierPoints.length
    ? shape.bezierPoints.flatMap((point) => [point.x, point.y])
    : shape.points;

  if (Array.isArray(coordinatePoints) && coordinatePoints.length >= 4) {
    const xs = coordinatePoints.filter((_, index) => index % 2 === 0).map(Number).filter(Number.isFinite);
    const ys = coordinatePoints.filter((_, index) => index % 2 === 1).map(Number).filter(Number.isFinite);
    if (xs.length && ys.length) {
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
    }
  }

  const radius = Math.max(1, Math.abs(shape.radius || shape.outerRadius || 1));
  return { x: -radius, y: -radius, width: radius * 2, height: radius * 2 };
};

const makeFillPattern = (style, background, foreground, size, spacing, rotation) => {
  if (typeof document === 'undefined') return null;
  const safeSize = Math.max(3, Math.min(60, Number(size) || 10));
  const safeSpacing = Math.max(0, Math.min(60, Number(spacing) || 0));
  const tileSize = Math.max(8, Math.ceil(safeSize + safeSpacing));
  const key = [style, background, foreground, safeSize, safeSpacing, rotation].join('|');
  if (fillPatternCache.has(key)) return fillPatternCache.get(key);

  const canvas = document.createElement('canvas');
  canvas.width = tileSize;
  canvas.height = tileSize;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, tileSize, tileSize);
  ctx.strokeStyle = foreground;
  ctx.fillStyle = foreground;
  ctx.lineWidth = Math.max(1, safeSize * 0.18);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const c = tileSize / 2;
  const r = Math.min(safeSize / 2, tileSize * 0.42);
  ctx.save();

  if (style === 'dots') {
    ctx.beginPath(); ctx.arc(c, c, r, 0, Math.PI * 2); ctx.fill();
  } else if (style === 'squares') {
    ctx.fillRect(c - r, c - r, r * 2, r * 2);
  } else if (style === 'hearts') {
    const heartSize = r * 1.7;
    ctx.beginPath();
    ctx.moveTo(c, c + heartSize * 0.65);
    ctx.bezierCurveTo(c - heartSize, c, c - heartSize * 0.55, c - heartSize * 0.65, c, c - heartSize * 0.18);
    ctx.bezierCurveTo(c + heartSize * 0.55, c - heartSize * 0.65, c + heartSize, c, c, c + heartSize * 0.65);
    ctx.fill();
  } else if (style === 'stars') {
    ctx.beginPath();
    for (let i = 0; i < 10; i += 1) {
      const angle = -Math.PI / 2 + (i * Math.PI) / 5;
      const pointRadius = i % 2 === 0 ? r : r * 0.45;
      const x = c + Math.cos(angle) * pointRadius;
      const y = c + Math.sin(angle) * pointRadius;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fill();
  } else if (style === 'lines') {
    ctx.beginPath(); ctx.moveTo(0, c); ctx.lineTo(tileSize, c); ctx.stroke();
  } else if (style === 'grid') {
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(tileSize, 0); ctx.moveTo(0, 0); ctx.lineTo(0, tileSize); ctx.stroke();
  } else if (style === 'checks') {
    ctx.fillRect(0, 0, c, c); ctx.fillRect(c, c, c, c);
  } else if (style === 'diagonalLines') {
    ctx.beginPath();
    ctx.moveTo(-tileSize * 0.25, tileSize); ctx.lineTo(tileSize * 0.75, 0);
    ctx.moveTo(tileSize * 0.25, tileSize); ctx.lineTo(tileSize * 1.25, 0);
    ctx.stroke();
  } else if (style === 'crosshatch') {
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(tileSize, tileSize); ctx.moveTo(tileSize, 0); ctx.lineTo(0, tileSize); ctx.stroke();
  } else if (style === 'waves') {
    ctx.beginPath();
    ctx.moveTo(0, c);
    ctx.bezierCurveTo(tileSize * 0.25, c - r, tileSize * 0.25, c + r, tileSize * 0.5, c);
    ctx.bezierCurveTo(tileSize * 0.75, c - r, tileSize * 0.75, c + r, tileSize, c);
    ctx.stroke();
  } else if (style === 'zigzags') {
    ctx.beginPath(); ctx.moveTo(0, c + r); ctx.lineTo(c, c - r); ctx.lineTo(tileSize, c + r); ctx.stroke();
  } else if (style === 'confetti') {
    ctx.lineWidth = Math.max(1.5, safeSize * 0.2);
    ctx.beginPath();
    ctx.moveTo(tileSize * 0.15, tileSize * 0.25); ctx.lineTo(tileSize * 0.35, tileSize * 0.12);
    ctx.moveTo(tileSize * 0.62, tileSize * 0.24); ctx.lineTo(tileSize * 0.75, tileSize * 0.42);
    ctx.moveTo(tileSize * 0.25, tileSize * 0.7); ctx.lineTo(tileSize * 0.42, tileSize * 0.82);
    ctx.moveTo(tileSize * 0.72, tileSize * 0.68); ctx.lineTo(tileSize * 0.88, tileSize * 0.58);
    ctx.stroke();
  }

  ctx.restore();
  fillPatternCache.set(key, canvas);
  return canvas;
};

const getShapeFillProps = (shape) => {
  const style = shape.fillStyle || 'solid';
  const primary = shape.fillColor || '#ffffff';
  const secondary = shape.fillSecondaryColor || '#F2D6A7';
  const bounds = getShapeFillBounds(shape);
  if (style === 'solid') return { fill: primary, fillPriority: 'color' };

  if (['horizontalGradient', 'verticalGradient', 'diagonalGradient'].includes(style)) {
    const endPoint = style === 'horizontalGradient'
      ? { x: bounds.x + bounds.width, y: bounds.y }
      : style === 'verticalGradient'
        ? { x: bounds.x, y: bounds.y + bounds.height }
        : { x: bounds.x + bounds.width, y: bounds.y + bounds.height };
    return {
      fill: primary,
      fillPriority: 'linear-gradient',
      fillLinearGradientStartPoint: { x: bounds.x, y: bounds.y },
      fillLinearGradientEndPoint: endPoint,
      fillLinearGradientColorStops: [0, primary, 1, secondary],
    };
  }

  if (style === 'radialGradient') {
    const centre = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
    return {
      fill: primary,
      fillPriority: 'radial-gradient',
      fillRadialGradientStartPoint: centre,
      fillRadialGradientStartRadius: 0,
      fillRadialGradientEndPoint: centre,
      fillRadialGradientEndRadius: Math.max(bounds.width, bounds.height) / 2,
      fillRadialGradientColorStops: [0, primary, 1, secondary],
    };
  }

  const pattern = makeFillPattern(style, primary, secondary, shape.fillPatternSize, shape.fillPatternSpacing, shape.fillPatternRotation);
  return pattern
    ? {
        fill: primary,
        fillPriority: 'pattern',
        fillPatternImage: pattern,
        fillPatternRepeat: 'repeat',
        fillPatternRotation: Number(shape.fillPatternRotation) || 0,
      }
    : { fill: primary, fillPriority: 'color' };
};

const TemplateImage = ({ shape }) => {
  const [image] = useImage(shape.src);
  return (
    <Image
      image={image}
      x={shape.x}
      y={shape.y}
      width={shape.width}
      height={shape.height}
      offsetX={shape.width / 2}
      offsetY={shape.height / 2}
      rotation={shape.rotation || 0}
      scaleX={shape.scaleX || 1}
      scaleY={shape.scaleY || 1}
      opacity={shape.opacity || 1}
    />
  );
};

const GridPreviewShape = ({ shape, common, stroke, strokeWidth, dash }) => {
  const width = shape.width || 0;
  const height = shape.height || 0;
  const rows = Math.max(1, parseInt(shape.gridRows, 10) || 3);
  const columns = Math.max(1, parseInt(shape.gridColumns, 10) || 3);

  const cellWidth = width / columns;
  const cellHeight = height / rows;

  const defaultCellFill = shape.fillColor || '#ffffff';
  const cellFills = Array.isArray(shape.gridCellFills)
    ? shape.gridCellFills
    : [];

  return (
    <Group {...common}>
      {Array.from({ length: rows * columns }).map((_, cellIndex) => {
        const row = Math.floor(cellIndex / columns);
        const col = cellIndex % columns;

        return (
          <Rect
            key={`${shape.id || 'grid'}-cell-${cellIndex}`}
            x={col * cellWidth}
            y={row * cellHeight}
            width={cellWidth}
            height={cellHeight}
            fill={
              shape.fillEnabled === false
                ? 'transparent'
                : (cellFills[cellIndex] || defaultCellFill)
            }
            {...(shape.fillEnabled === false ? {} : getShapeFillProps({
              ...shape,
              width: cellWidth,
              height: cellHeight,
              fillColor: cellFills[cellIndex] || defaultCellFill,
            }))}
            stroke="transparent"
          />
        );
      })}

      <Rect
        width={width}
        height={height}
        fill="transparent"
        stroke={stroke}
        strokeWidth={strokeWidth}
        dash={dash}
        
      />

      {Array.from({ length: rows - 1 }).map((_, rowIndex) => {
        const y = cellHeight * (rowIndex + 1);
        return (
          <Line
            key={`${shape.id || 'grid'}-row-${rowIndex}`}
            points={[0, y, width, y]}
            stroke={stroke}
            strokeWidth={strokeWidth}
            dash={dash}
            
          />
        );
      })}

      {Array.from({ length: columns - 1 }).map((_, colIndex) => {
        const x = cellWidth * (colIndex + 1);
        return (
          <Line
            key={`${shape.id || 'grid'}-col-${colIndex}`}
            points={[x, 0, x, height]}
            stroke={stroke}
            strokeWidth={strokeWidth}
            dash={dash}
            
          />
        );
      })}
    </Group>
  );
};

const CircleSegmentsPreviewShape = ({ shape, common, fill, stroke, strokeWidth, dash }) => {
  const radius = shape.radius || 0;
  const segments = Math.max(1, shape.segments || 4);

  return (
    <Shape
      {...common}
      sceneFunc={(ctx, node) => {
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, Math.PI * 2, false);

        // Segment divider lines
        for (let i = 0; i < segments; i++) {
          const angle = (Math.PI * 2 * i) / segments;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          ctx.moveTo(0, 0);
          ctx.lineTo(x, y);
        }

        if (shape.fillEnabled !== false) {
          ctx.fillShape(node);
        }
        if (shape.strokeEnabled !== false) {
          ctx.strokeShape(node);
        }
      }}
      fill={fill}
      {...(shape.fillEnabled === false ? {} : getShapeFillProps(shape))}
      stroke={stroke}
      strokeWidth={strokeWidth}
      dash={dash}
    />
  );
};

const MazePreviewShape = ({ shape, common, fill, stroke, strokeWidth, dash }) => {
  const width = shape.width || 0;
  const height = shape.height || 0;
  const walls = Array.isArray(shape.mazeWalls) ? shape.mazeWalls : [];

  return (
    <Shape
      {...common}
      sceneFunc={(ctx, node) => {
        ctx.beginPath();

        if (shape.fillEnabled !== false) {
          ctx.rect(0, 0, width, height);
          ctx.fillShape(node);
        }

        if (shape.strokeEnabled !== false) {
          walls.forEach((wall) => {
            if (!Array.isArray(wall) || wall.length !== 4) return;

            ctx.moveTo(wall[0], wall[1]);
            ctx.lineTo(wall[2], wall[3]);
          });

          ctx.strokeShape(node);
        }
      }}
      fill={fill}
      {...(shape.fillEnabled === false ? {} : getShapeFillProps(shape))}
      stroke={stroke}
      strokeWidth={strokeWidth}
      dash={dash}
    />
  );
};

const WordSearchPreviewShape = ({ shape, common, fill, stroke, strokeWidth, dash }) => {
  const width = shape.width || 0;
  const height = shape.height || 0;
  const rows = Math.max(1, shape.wordSearchRows || 5);
  const columns = Math.max(1, shape.wordSearchColumns || 5);
  const cellWidth = columns > 0 ? width / columns : 0;
  const cellHeight = rows > 0 ? height / rows : 0;
  const letters = Array.isArray(shape.wordSearchLetters) ? shape.wordSearchLetters : [];

  return (
    <Group {...common}>
      <Rect
        width={width}
        height={height}
        fill={shape.fillEnabled === false ? 'transparent' : fill}
        {...(shape.fillEnabled === false ? {} : getShapeFillProps(shape))}
        stroke={stroke}
        strokeWidth={strokeWidth}
        dash={dash}
        strokeScaleEnabled={true}
      />

      {Array.from({ length: rows - 1 }).map((_, rowIndex) => {
        const y = cellHeight * (rowIndex + 1);
        return (
          <Line
            key={`${shape.id || 'wordsearch'}-row-${rowIndex}`}
            points={[0, y, width, y]}
            stroke={stroke}
            strokeWidth={strokeWidth}
            dash={dash}
            strokeScaleEnabled={true}
          />
        );
      })}

      {Array.from({ length: columns - 1 }).map((_, colIndex) => {
        const x = cellWidth * (colIndex + 1);
        return (
          <Line
            key={`${shape.id || 'wordsearch'}-col-${colIndex}`}
            points={[x, 0, x, height]}
            stroke={stroke}
            strokeWidth={strokeWidth}
            dash={dash}
            strokeScaleEnabled={true}
          />
        );
      })}

      {Array.from({ length: rows * columns }).map((_, cellIndex) => {
        const row = Math.floor(cellIndex / columns);
        const col = cellIndex % columns;
        const x = col * cellWidth;
        const y = row * cellHeight;
        const letter = letters[cellIndex] || '';

        return (
          <Text
            key={`${shape.id || 'wordsearch'}-letter-${cellIndex}`}
            x={x}
            y={y}
            width={cellWidth}
            height={cellHeight}
            text={letter}
            align="center"
            verticalAlign="middle"
            fontSize={Math.max(12, Math.min(cellWidth, cellHeight) * 0.45)}
            fontFamily={shape.wordSearchFontFamily || 'Arial'}
            fill="#000000"
          />
        );
      })}
    </Group>
  );
};

const PreviewCanvas = ({ page, maxWidth = 200, maxHeight = 280 }) => {
  const stageRef = useRef(null);
  const [fontsReady, setFontsReady] = useState(false);

  const fontsUsed = useMemo(() => {
    if (!page?.shapes) return APP_FONT_FAMILIES;

    const foundFonts = new Set(APP_FONT_FAMILIES);

    page.shapes.forEach((shape) => {
      if (shape.type === 'text' && shape.fontFamily) {
        foundFonts.add(shape.fontFamily);
      }

      if (shape.type === 'wordSearch' && shape.wordSearchFontFamily) {
        foundFonts.add(shape.wordSearchFontFamily);
      }

      if (shape.type === 'maskedGroup' && Array.isArray(shape.children)) {
        shape.children.forEach((child) => {
          if (child.type === 'text' && child.fontFamily) {
            foundFonts.add(child.fontFamily);
          }

          if (child.type === 'wordSearch' && child.wordSearchFontFamily) {
            foundFonts.add(child.wordSearchFontFamily);
          }
        });
      }
    });

    return Array.from(foundFonts);
  }, [page]);

  useEffect(() => {
    let cancelled = false;

    const loadPreviewFonts = async () => {
      setFontsReady(false);

      try {
        await preloadAppFonts();

        if (document.fonts) {
          await Promise.all(
            fontsUsed.map((fontFamily) =>
              document.fonts.load(`16px "${fontFamily}"`)
            )
          );

          await document.fonts.ready;
        }

        if (!cancelled) {
          setFontsReady(true);
        }
      } catch (error) {
        console.warn('Preview font loading failed:', error);

        if (!cancelled) {
          setFontsReady(true);
        }
      }
    };

    loadPreviewFonts();

    return () => {
      cancelled = true;
    };
  }, [fontsUsed]);

  useEffect(() => {
    if (!fontsReady) return;

  requestAnimationFrame(() => {
    if (stageRef.current) {
      stageRef.current.batchDraw();
    }
  });
  }, [fontsReady, page]);

  if (!page || !page.shapes) return null;
  if (!fontsReady) return null;

  const pageWidth = page.width || 794;
  const pageHeight = page.height || 1123;

  const scale = Math.min(maxWidth / pageWidth, maxHeight / pageHeight);
  const previewWidth = pageWidth * scale;
  const previewHeight = pageHeight * scale;

  return (
    <Stage
      ref={stageRef}
      width={previewWidth}
      height={previewHeight}
      scaleX={scale}
      scaleY={scale}
    >
      <Layer>
        {page.shapes.map((shape, i) => {
          const shapeKey = shape.id || `${shape.type}-${i}`;
          const common = {
            x: shape.x || 0,
            y: shape.y || 0,
            rotation: shape.rotation || 0,
            scaleX: shape.scaleX || 1,
            scaleY: shape.scaleY || 1,
            opacity: Number(shape.opacity ?? 1),
          };

          const fill =
            shape.fillEnabled === false
              ? 'transparent'
              : (shape.fillColor || shape.fill || '#ccc');

          const fillProps = shape.fillEnabled === false
            ? { fill: 'transparent' }
            : getShapeFillProps(shape);

          const stroke =
            shape.strokeEnabled === false
              ? null
              : (shape.strokeColor || 'black');

          const strokeWidth =
            shape.strokeEnabled === false
              ? 0
              : (shape.strokeWidth || 2);

          const previewStrokeWidth = strokeWidth;
          const dash = shape.dashEnabled ? (shape.dash || [10, 5]) : [];

          if (shape.type === 'maskedGroup' && Array.isArray(shape.children)) {
            const [maskShape, contentShape] = shape.children;
            const groupKey = `masked-${shape.id}`;

            const offsetX = (maskShape.x || 0) - (contentShape.x || 0);
            const offsetY = (maskShape.y || 0) - (contentShape.y || 0);

            const clipFunc = (ctx) => {
              ctx.beginPath();

              if (shape.reverseMask) {
                ctx.rect(
                  -contentShape.width / 2 * (contentShape.scaleX || 1),
                  -contentShape.height / 2 * (contentShape.scaleY || 1),
                  contentShape.width * (contentShape.scaleX || 1),
                  contentShape.height * (contentShape.scaleY || 1)
                );
              }

              if (maskShape.type === 'pen' && Array.isArray(maskShape.points)) {
                const points = maskShape.points;
                ctx.moveTo(points[0] + offsetX, points[1] + offsetY);
                for (let i = 2; i < points.length; i += 2) {
                  ctx.lineTo(points[i] + offsetX, points[i + 1] + offsetY);
                }
                ctx.closePath();
              } else if (maskShape.type === 'rect') {
                ctx.rect(offsetX, offsetY, maskShape.width, maskShape.height);
              }

              ctx.clip(shape.reverseMask ? 'evenodd' : 'nonzero');
            };

            const contentProps = {
              x: 0,
              y: 0,
              width: contentShape.width,
              height: contentShape.height,
              opacity: contentShape.opacity ?? 1,
              stroke: contentShape.strokeColor,
              strokeWidth: contentShape.strokeWidth || 2,
              fill: contentShape.fillColor,
              ...(contentShape.fillEnabled === false ? { fill: 'transparent' } : getShapeFillProps(contentShape)),
            };

            return (
              <Group
                key={groupKey}
                x={shape.x}
                y={shape.y}
                scaleX={shape.scaleX || 1}
                scaleY={shape.scaleY || 1}
                rotation={shape.rotation || 0}
                clipFunc={clipFunc}
              >
                {(() => {
                  switch (contentShape.type) {
                    case 'image':
                      return (
                        <TemplateImage
                          key={contentShape.id}
                          shape={{ ...contentShape, x: 0, y: 0 }}
                        />
                      );
                    case 'rect':
                      return <Rect key={contentShape.id} {...contentProps} />;
                    case 'roundedRect':
                      return (
                        <Rect
                          key={contentShape.id}
                          {...contentProps}
                          cornerRadius={contentShape.cornerRadius || 0}
                        />
                      );
                    case 'circle':
                      return (
                        <Circle
                          key={contentShape.id}
                          {...contentProps}
                          radius={contentShape.radius}
                        />
                      );
                    case 'text':
                      return (
                        <Text
                          key={contentShape.id}
                          text={contentShape.text}
                          fontSize={contentShape.fontSize}
                          fontFamily={contentShape.fontFamily}
                          fill={contentShape.fill || contentProps.fill}
                          fontStyle={`${contentShape.bold ? 'bold' : ''} ${contentShape.italic ? 'italic' : ''}`.trim()}
                          textDecoration={contentShape.underline ? 'underline' : ''}
                          align={contentShape.align || 'left'}
                          {...contentProps}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </Group>
            );
          }

          switch (shape.type) {
            case 'rect':
              return (
                <Rect
                  key={shapeKey}
                  {...common}
                  width={shape.width}
                  height={shape.height}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'roundedRect':
              return (
                <Rect
                  key={shapeKey}
                  {...common}
                  width={shape.width}
                  height={shape.height}
                  cornerRadius={shape.cornerRadius || 10}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'grid':
              return (
                <GridPreviewShape
                  key={shapeKey}
                  shape={shape}
                  common={common}
                  stroke={stroke}
                  strokeWidth={previewStrokeWidth}
                  dash={dash}
                />
              );

            case 'circle':
              return (
                <Circle
                  key={shapeKey}
                  {...common}
                  radius={shape.radius}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'semiCircle':
              return (
                <Wedge
                  key={shapeKey}
                  {...common}
                  radius={shape.radius}
                  angle={shape.arcAngle || 180}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={previewStrokeWidth}
                  dash={dash}
                />
              );

            case 'circleSegments':
              return (
                <CircleSegmentsPreviewShape
                  key={shapeKey}
                  shape={shape}
                  common={common}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'maze':
              return (
                <MazePreviewShape
                  key={shapeKey}
                  shape={shape}
                  common={common}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'wordSearch':
              return (
                <WordSearchPreviewShape
                  key={shapeKey}
                  shape={shape}
                  common={common}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={previewStrokeWidth}
                  dash={dash}
                />
              );

            case 'triangle': {
              const radius = shape.radius || 1;
              const peakOffset =
                shape.trianglePeakOffset !== undefined
                  ? shape.trianglePeakOffset
                  : 0;

              return (
                <Line
                  key={shapeKey}
                  {...common}
                  points={[
                    peakOffset, -radius,
                    radius, radius,
                    -radius, radius,
                  ]}
                  closed
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                  lineJoin="round"
                  lineCap="round"
                />
              );
            }

            case 'pentagon':
              return (
                <RegularPolygon
                  key={shapeKey}
                  {...common}
                  sides={5}
                  radius={shape.radius}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'hexagon':
              return (
                <RegularPolygon
                  key={shapeKey}
                  {...common}
                  sides={6}
                  radius={shape.radius}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'octagon':
              return (
                <RegularPolygon
                  key={shapeKey}
                  {...common}
                  sides={8}
                  radius={shape.radius}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'star':
              return (
                <Star
                  key={shapeKey}
                  {...common}
                  numPoints={shape.numPoints || 5}
                  innerRadius={
                    shape.innerRadius > 0
                      ? shape.innerRadius
                      : (shape.outerRadius || 50) / 2
                  }
                  outerRadius={shape.outerRadius || 50}
                  fill={fill}
                  {...fillProps}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  dash={dash}
                />
              );

            case 'line':
              return (
                <Line
                  key={shapeKey}
                  {...common}
                  points={shape.points}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  lineCap="round"
                  lineJoin="round"
                  dash={dash}
                />
              );

            case 'pencil':
              return (
                <Line
                  key={shapeKey}
                  {...common}
                  points={shape.points}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  tension={shape.tension || 0.5}
                  lineCap={shape.lineCap || 'round'}
                  lineJoin={shape.lineJoin || 'round'}
                  dash={dash}
                />
              );

            case 'text':
              return (
                <Text
                  key={shapeKey}
                  {...common}
                  text={shape.text}
                  align={shape.align || 'left'}
                  fontSize={shape.fontSize || 16}
                  fontFamily={shape.fontFamily || 'Arial'}
                  fill={shape.fill || '#000'}
                  fontStyle={`${shape.bold ? 'bold' : ''} ${shape.italic ? 'italic' : ''}`.trim()}
                  textDecoration={shape.underline ? 'underline' : ''}
                />
              );

            case 'pen': {
              if (shape.bezierEnabled && Array.isArray(shape.bezierPoints)) {
                return (
                  <Shape
                    key={shapeKey}
                    {...common}
                    
                    fill={
                      shape.fillEnabled === false
                        ? 'transparent'
                        : (shape.fillColor || 'transparent')
                    }
                    {...fillProps}
                    stroke={
                      shape.strokeEnabled === false
                        ? null
                        : (shape.strokeColor || 'black')
                    }
                    strokeWidth={
                      shape.strokeEnabled === false
                        ? 0
                        : (shape.strokeWidth || 2)
                    }
                    dash={
                      shape.dashEnabled
                        ? shape.dash || [10, 5]
                        : []
                    }
                    sceneFunc={(ctx, node) => {
                      const points = shape.bezierPoints;

                      if (!points.length) return;

                      ctx.beginPath();
                      ctx.moveTo(points[0].x, points[0].y);

                      const isClosed = shape.closed === true;
                      const segmentCount = isClosed ? points.length : points.length - 1;

                      for (let i = 0; i < segmentCount; i++) {
                        const current = points[i];
                        const next = isClosed ? points[(i + 1) % points.length] : points[i + 1];

                        if (!current || !next) continue;

                        ctx.bezierCurveTo(
                          current.x + (current.out?.x || 0),
                          current.y + (current.out?.y || 0),
                          next.x + (next.in?.x || 0),
                          next.y + (next.in?.y || 0),
                          next.x,
                          next.y
                        );
                      }

                      if (isClosed) {
                        ctx.closePath();
                        ctx.fillStrokeShape(node);
                      } else {
                        ctx.strokeShape(node);
                      }
                    }}
                  />
                );
              }

              return (
                <Line
                  key={shapeKey}
                  {...common}
                  points={shape.points}
                  closed={shape.closed === true}
                  fill={
                    shape.fillEnabled === false
                      ? 'transparent'
                      : (shape.fillColor || 'transparent')
                  }
                  {...fillProps}
                  stroke={
                    shape.strokeEnabled === false
                      ? null
                      : (shape.strokeColor || 'black')
                  }
                  strokeWidth={
                    shape.strokeEnabled === false
                      ? 0
                      : (shape.strokeWidth || 2)
                  }
                  tension={shape.tension || 0}
                  lineCap="round"
                  lineJoin="round"
                  dash={
                    shape.dashEnabled
                      ? shape.dash || [10, 5]
                      : []
                  }
                />
              );
            }

            case 'image':
              return <TemplateImage key={shapeKey} shape={shape} />;

            default:
              return null;
          }
        })}
      </Layer>
    </Stage>
  );
};

export default PreviewCanvas;
